// Pure logic behind the practice player's A–B loop and speed chips

// A loop shorter than this would seek faster than YouTube can buffer
export const MIN_LOOP_LENGTH = 1;
export const NUDGE_SECONDS = 0.5;
// Repeats with no one touching a control before the loop pauses to ask whether anyone is still there
export const MAX_UNATTENDED_LOOPS = 100;

// YouTube's usual list, offered until the player says (and when it can't). Matching it means the speed
// slider is already laid out as it will be, so nothing moves when the player's real list arrives.
const DEFAULT_RATES = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
const MIN_PRACTICE_RATE = 0.25;
const MAX_PRACTICE_RATE = 2;

/** Past the end of the loop, so the player should go back to its start */
export const shouldLoopBack = (time, a, b) => b > a && time >= b;

/**
 * Keeps a loop inside the video and at least `minLength` long. Moving `edge` ('a' or 'b') never pushes
 * the other one; the moved edge stops short of it instead.
 */
export const clampRange = (a, b, duration, edge = 'b', minLength = MIN_LOOP_LENGTH) => {
  const end = duration > 0 ? duration : Math.max(a, b);
  const length = Math.min(minLength, end);
  const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);

  if (edge === 'a') {
    const nextB = clamp(b, length, end);
    return { a: clamp(a, 0, nextB - length), b: nextB };
  }

  const nextA = clamp(a, 0, end - length);
  return { a: nextA, b: clamp(b, nextA + length, end) };
};

/** 83.4 → "1:23.4"; a whole second drops the tenth. Hours roll into minutes, as songs rarely run that long */
export const formatTime = (seconds) => {
  const safe = Math.max(0, Number.isFinite(seconds) ? seconds : 0);
  const tenths = Math.round(safe * 10);
  const minutes = Math.floor(tenths / 600);
  const secs = Math.floor((tenths % 600) / 10);
  const tenth = tenths % 10;

  return `${minutes}:${String(secs).padStart(2, '0')}${tenth ? `.${tenth}` : ''}`;
};

/** The speeds worth practicing at, out of what the player offers: everything from a quarter to double */
export const practiceRates = (rates) => {
  const usable = (rates || []).filter((r) => r >= MIN_PRACTICE_RATE && r <= MAX_PRACTICE_RATE);

  return usable.length ? [...usable].sort((x, y) => x - y) : DEFAULT_RATES;
};

/** 0.75 → "0.75×", 1 → "1×" */
export const formatRate = (rate) => `${rate}×`;

/** Where `rate` sits in `rates`, or the nearest one when the player reports a speed not on the list */
export const rateIndex = (rates, rate) => {
  if (!rates.length) return 0;
  return rates.reduce((best, r, i) => (Math.abs(r - rate) < Math.abs(rates[best] - rate) ? i : best), 0);
};

/** 'slower', 'normal' or 'faster' than the video's own speed — what the speed slider is colored by */
export const rateTone = (rate) => {
  if (rate < 1) return 'slower';
  return rate > 1 ? 'faster' : 'normal';
};
